import type { QueryClient } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';

/**
 * Cache identity — the "who is this cache for?" discriminator.
 *
 * Delogue 2.0 shares a session with Delogue legacy. A user can log out and back in as
 * somebody else entirely on the legacy side (shared Forms cookie), or clear the cookie by
 * hand, WITHOUT this SPA ever reloading. When that happens the tab keeps rendering the
 * previous user's cached data
 *
 * TanStack's own cache-busting (`persistOptions.buster`) does NOT cover this: the buster is
 * compared only on mouth
 */
export const CACHE_IDENTITY_KEY = 'delogue-cache-identity';

/**
 * `null` when there is no resolved login context yet (logged out, or mid-load). Callers must
 * treat `null` as "don't know" and skip the comparison — never as "changed".
 */
export const getCacheIdentity = (
  loginContextId: string | null | undefined,
  organizationId: number | null | undefined
): string | null => {
  if (!loginContextId || organizationId == null) return null;
  return `${APP_VERSION}|${loginContextId}|${organizationId}`;
};

export const readStoredCacheIdentity = (): string | null => {
  try {
    return localStorage.getItem(CACHE_IDENTITY_KEY);
  } catch {
    // Safari private mode / storage disabled — degrade to "no marker" rather than crashing
    // the app shell. Worst case we skip a bust we would otherwise have done.
    return null;
  }
};

export const writeStoredCacheIdentity = (identity: string): void => {
  try {
    localStorage.setItem(CACHE_IDENTITY_KEY, identity);
  } catch {
    // See readStoredCacheIdentity.
  }
};

/**
 * Forget which identity the cache belonged to. Called on an explicit logout
 */
export const clearStoredCacheIdentity = (): void => {
  try {
    localStorage.removeItem(CACHE_IDENTITY_KEY);
  } catch {
    // See readStoredCacheIdentity.
  }
};

/**
 * Drop every cached query — in memory AND on disk — then record the identity the fresh cache
 * belongs to.
 *
 * Order matters. `queryClient.clear()` empties the QueryCache, which makes
 * PersistQueryClientProvider's subscription persist that now-empty cache; `removeClient()`
 * afterwards deletes the IndexedDB entry outright so a reload cannot rehydrate anything the
 * previous user left behind.
 *
 * The marker is only committed once the delete has actually succeeded
 */
export const bustCacheForIdentity = async (
  queryClient: QueryClient,
  persister: Persister,
  identity: string
): Promise<void> => {
  queryClient.clear();
  try {
    await persister.removeClient();
  } catch {
    return;
  }
  writeStoredCacheIdentity(identity);
};
